import type { TFunction } from 'i18next';
import type { BondType } from '@peruselab/api-client/bondTypes';
import type { MoleculeComponentEntry, MoleculeFacts, MoleculeSummary } from '@peruselab/api-client/molecules';
import type { PublicReactionPage } from '@peruselab/api-client/reactions';
import { CATALOGUE_ORIGIN, catalogueMoleculePath, publicDrawingPath, publicReactionPath } from './publicAccess';

/** What a public molecule page tells search engines and link previews (ADR 0023 D2, D3). */
export interface CatalogueHead {
  readonly title: string;
  readonly description: string;
  readonly canonical: string;
  /** #1119 (D5): the structure as a picture, for link previews -- with its alternative text. Null for
   *  a page without one (#1126: the bond glossary), which then gets the small card. */
  readonly image: string | null;
  readonly imageAlt: string | null;
  readonly jsonLd: Readonly<Record<string, unknown>>;
}

interface MoleculeHeadInput {
  readonly summary: MoleculeSummary;
  readonly facts: MoleculeFacts | null;
  /** A salt's ions the reader may open (#1086); empty for anything else. */
  readonly components: readonly MoleculeComponentEntry[];
}

const MAX_SYNONYMS = 10;
const WEIGHT_DIGITS = 3;

export function catalogueUrl(moleculeId: string): string {
  return CATALOGUE_ORIGIN + catalogueMoleculePath(moleculeId);
}

/**
 * ADR 0023 (#1113): a molecule page's head. D3: the title and description lead with what
 * researchers search by -- the name and the exact InChIKey, or the formula when there's no name --
 * and the canonical address is the apex's `/m/<id>` (owner, 2026-10-29). D2: Schema.org JSON-LD,
 * a `MolecularEntity`; several pieces (a salt) also a `ChemicalSubstance` made of them.
 */
export function moleculeHead(t: TFunction, { summary, facts, components }: MoleculeHeadInput): CatalogueHead {
  const canonical = catalogueUrl(summary.id);
  const name = facts?.title ?? summary.name ?? null;
  const inchiKey = summary.inchiKey ?? facts?.inchiKey ?? null;
  const label = name ?? summary.molecularFormula ?? summary.id;
  const weight = summary.molecularWeight === null ? null : Number(summary.molecularWeight.toFixed(WEIGHT_DIGITS));

  const title = inchiKey
    ? t('catalogue.titleWithKey', { name: label, inchiKey })
    : t('catalogue.title', { name: label });
  const facets = [
    summary.molecularFormula,
    weight === null ? null : t('catalogue.weight', { weight }),
    inchiKey ? t('catalogue.inchiKey', { inchiKey }) : null
  ].filter((facet): facet is string => facet !== null);
  const description = t('catalogue.description', { name: label, facets: facets.join(', ') });

  const pieces = summary.smiles?.split('.') ?? [];
  const parts =
    components.length > 0
      ? components.map((component) => ({
          '@type': 'MolecularEntity',
          // #1115: an ion the reader may not open is named, not linked.
          ...(component.id ? { '@id': catalogueUrl(component.id), url: catalogueUrl(component.id) } : {}),
          ...(component.name ? { name: component.name } : {}),
          ...(component.molecularFormula ? { molecularFormula: component.molecularFormula } : {})
        }))
      : pieces.length > 1
        ? pieces.map((smiles) => ({ '@type': 'MolecularEntity', smiles }))
        : [];
  const synonyms = (facts?.synonyms ?? []).filter((synonym) => synonym !== name).slice(0, MAX_SYNONYMS);

  const jsonLd: Record<string, unknown> = {
    '@context': 'https://schema.org',
    '@type': parts.length > 0 ? ['MolecularEntity', 'ChemicalSubstance'] : 'MolecularEntity',
    '@id': canonical,
    url: canonical,
    name: label,
    image: `${canonical}/depiction.png`,
    // The summary's name is PubChem's IUPAC-style one; the page's title is its display title (#906).
    ...(summary.name ? { iupacName: summary.name } : {}),
    ...(synonyms.length > 0 ? { alternateName: synonyms } : {}),
    ...(summary.inchi ? { inChI: summary.inchi } : {}),
    ...(inchiKey ? { inChIKey: inchiKey } : {}),
    ...(summary.smiles ? { smiles: summary.smiles } : {}),
    ...(summary.molecularFormula ? { molecularFormula: summary.molecularFormula } : {}),
    ...(weight === null ? {} : { molecularWeight: { '@type': 'QuantitativeValue', value: weight, unitText: 'g/mol' } }),
    ...(summary.pubchemCid ? { sameAs: [`https://pubchem.ncbi.nlm.nih.gov/compound/${summary.pubchemCid}`] } : {}),
    ...(parts.length > 0 ? { hasBioChemEntityPart: parts } : {})
  };
  return {
    title,
    description,
    canonical,
    image: `${canonical}/depiction.png`,
    imageAlt: t('catalogue.imageAlt', { name: label }),
    jsonLd
  };
}

interface DrawingHeadInput {
  readonly id: string;
  readonly title: string | null;
  readonly author: string | null;
  readonly updatedAt: string;
  /** The structure's formula and SMILES, as the page shows them; null if they couldn't be computed. */
  readonly formula: string | null;
  readonly smiles: string | null;
}

/**
 * ADR 0023 (#1123): a public drawing's head, at `/i/<id>` -- the same title, description and JSON-LD
 * the server writes into the page (CataloguePage.renderDrawing): a Schema.org `CreativeWork` by its
 * author, `about` the structure on it.
 */
export function drawingHead(t: TFunction, drawing: DrawingHeadInput): CatalogueHead {
  const canonical = CATALOGUE_ORIGIN + publicDrawingPath(drawing.id);
  const label = drawing.title?.trim() || drawing.formula || t('catalogue.drawing.untitled');
  const title = drawing.author
    ? t('catalogue.drawing.titleBy', { name: label, author: drawing.author })
    : t('catalogue.title', { name: label });
  const description = drawing.author
    ? t('catalogue.drawing.descriptionBy', { name: label, author: drawing.author })
    : t('catalogue.drawing.description', { name: label });
  const about =
    drawing.formula || drawing.smiles
      ? [
          {
            '@type': 'MolecularEntity',
            ...(drawing.formula ? { molecularFormula: drawing.formula } : {}),
            ...(drawing.smiles ? { smiles: drawing.smiles } : {})
          }
        ]
      : [];
  return {
    title,
    description,
    canonical,
    image: `${canonical}/depiction.png`,
    imageAlt: t('catalogue.drawing.imageAlt', { name: label }),
    jsonLd: {
      '@context': 'https://schema.org',
      '@type': 'CreativeWork',
      '@id': canonical,
      url: canonical,
      name: label,
      image: `${canonical}/depiction.png`,
      ...(drawing.author ? { author: { '@type': 'Person', name: drawing.author } } : {}),
      dateModified: new Date(drawing.updatedAt).toISOString().replace(/\.\d{3}Z$/, 'Z'),
      ...(about.length > 0 ? { about } : {})
    }
  };
}

/**
 * ADR 0023 (#1124): a public reaction's head, at `/r/<id>` -- the same title, description and
 * JSON-LD the server writes into the page (CataloguePage.renderReaction): Schema.org has no reaction
 * type, so a `CreativeWork` by its author, `about` its participants.
 */
export function reactionHead(t: TFunction, reaction: PublicReactionPage): CatalogueHead {
  const canonical = CATALOGUE_ORIGIN + publicReactionPath(reaction.id);
  const label = reaction.label;
  const author = reaction.authorName;
  const title = author
    ? t('catalogue.reaction.titleBy', { name: label, author })
    : t('catalogue.title', { name: label });
  const description = author
    ? t('catalogue.reaction.descriptionBy', { name: label, author })
    : t('catalogue.reaction.description', { name: label });
  const about = reaction.participants.map((participant) => ({
    '@type': 'MolecularEntity',
    ...(participant.moleculeId
      ? {
          '@id': CATALOGUE_ORIGIN + catalogueMoleculePath(participant.moleculeId),
          url: CATALOGUE_ORIGIN + catalogueMoleculePath(participant.moleculeId)
        }
      : {}),
    ...(participant.name ? { name: participant.name } : {}),
    ...(participant.molecularFormula ? { molecularFormula: participant.molecularFormula } : {}),
    ...(participant.smiles ? { smiles: participant.smiles } : {}),
    description: participant.role
  }));
  return {
    title,
    description,
    canonical,
    image: `${canonical}/depiction.png`,
    imageAlt: t('catalogue.reaction.imageAlt', { name: label }),
    jsonLd: {
      '@context': 'https://schema.org',
      '@type': 'CreativeWork',
      '@id': canonical,
      url: canonical,
      name: label,
      genre: 'Chemical reaction',
      image: `${canonical}/depiction.png`,
      ...(author ? { author: { '@type': 'Person', name: author } } : {}),
      dateModified: new Date(reaction.updatedAt).toISOString().replace(/\.\d{3}Z$/, 'Z'),
      ...(about.length > 0 ? { about } : {})
    }
  };
}

export const GLOSSARY_PATH = '/bonds';

/**
 * ADR 0023 D7 (#1126): the public bond glossary's head -- the same words the server writes
 * (GlossaryPage.renderGlossary): a Schema.org `DefinedTermSet` of every kind of bond.
 */
export function glossaryHead(t: TFunction, types: readonly BondType[]): CatalogueHead {
  const canonical = CATALOGUE_ORIGIN + GLOSSARY_PATH;
  const name = t('glossary.name');
  const description = t('glossary.description', { count: types.length });
  return {
    title: t('glossary.title', { count: types.length }),
    description,
    canonical,
    image: null,
    imageAlt: null,
    jsonLd: {
      '@context': 'https://schema.org',
      '@type': 'DefinedTermSet',
      '@id': canonical,
      url: canonical,
      name,
      description,
      hasDefinedTerm: types.map((type) => ({
        '@type': 'DefinedTerm',
        '@id': `${canonical}/${type.slug}`,
        url: `${canonical}/${type.slug}`,
        name: type.name,
        termCode: type.slug
      }))
    }
  };
}

/** #1126: one kind of bond's head (GlossaryPage.renderTerm): a `DefinedTerm` in the glossary. */
export function bondTermHead(t: TFunction, type: BondType): CatalogueHead {
  const glossary = CATALOGUE_ORIGIN + GLOSSARY_PATH;
  const canonical = `${glossary}/${type.slug}`;
  return {
    title: t('glossary.termTitle', { name: type.name }),
    description: type.definition,
    canonical,
    image: null,
    imageAlt: null,
    jsonLd: {
      '@context': 'https://schema.org',
      '@type': 'DefinedTerm',
      '@id': canonical,
      url: canonical,
      name: type.name,
      description: type.definition,
      termCode: type.slug,
      inDefinedTermSet: { '@type': 'DefinedTermSet', '@id': glossary, name: t('glossary.name') }
    }
  };
}
